import { logInfo, logError } from './loggerService';

/**
 * Nuclear-reset the PWA: unregister every service worker, delete every
 * CacheStorage entry, and reload with a cache-bust query so any URL-keyed
 * edge cache also misses. The user-visible result is identical to "the
 * app was just installed fresh" — even inside an installed PWA on iOS or
 * Chrome, no uninstall/reinstall gesture required.
 *
 * Used by:
 *   - `versionCheckService.runVersionCheck()` when /ping reports the FE is
 *     below the minimum supported version (automatic forced update).
 *   - `UpdateRequiredPage`'s "Apply Update" button (user-triggered retry
 *     after an automatic attempt failed to stick).
 *
 * Returns a never-resolving promise on success because the page is about
 * to navigate. Logs and rejects only if the unregister/delete chain itself
 * throws — in which case the caller should still try to reload as a
 * last-ditch fallback.
 */
export async function nuclearReset(): Promise<never> {
  logInfo('nuclearReset: clearing service workers and caches');

  try {
    if ('serviceWorker' in navigator) {
      const regs = await navigator.serviceWorker.getRegistrations();
      await Promise.all(regs.map((r) => r.unregister().catch(() => false)));
      logInfo(`nuclearReset: unregistered ${regs.length} service worker(s)`);
    }
  } catch (err) {
    logError(err instanceof Error ? err : new Error(String(err)), 'nuclearReset: failed to unregister service workers');
  }

  try {
    if ('caches' in window) {
      const names = await caches.keys();
      await Promise.all(names.map((n) => caches.delete(n).catch(() => false)));
      logInfo(`nuclearReset: deleted ${names.length} cache(s)`);
    }
  } catch (err) {
    logError(err instanceof Error ? err : new Error(String(err)), 'nuclearReset: failed to delete caches');
  }

  // Reload with a cache-bust query param. `replace` (not assign) so this
  // intermediate state isn't left in the history stack. Even if some edge
  // proxy somehow ignored the no-cache header on index.html, the unique
  // query string guarantees a cache miss at every layer.
  const target = `${window.location.origin}/?_bust=${Date.now()}`;
  logInfo(`nuclearReset: reloading to ${target}`);
  window.location.replace(target);

  // Return a never-resolving promise so callers that await this don't
  // race against the reload (which would tear down the JS context anyway).
  return new Promise<never>(() => {
    /* never */
  });
}
